'use client';

import { useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { ShoppingBag } from 'lucide-react';
import { setReferralCookie } from '@/lib/referral-cookie';
import { sendHubVisitBeacon } from '@/lib/api-client/namespaces/hub-crm';

/**
 * VisitMarketButton — "Visita mi Market" CTA for the public profile page.
 *
 * SPEC-094 §1.4 (Market Link en /p/{username}): a button on the public
 * profile page that links to `/market?ref={username}`. When clicked,
 * the button sets the `profileia_ref` cookie (30 días, SameSite=Lax,
 * Secure si HTTPS — same cookie used by /r/[code]) BEFORE navigating,
 * so the visitor's eventual registration is attributed to the profile
 * owner.
 *
 * SPEC-119 Task E (Analytics tracking): the click handler also fires
 * `POST /api/public/:username/click` with `{ target: 'market' }` body
 * to increment `stats.cardClicks` on the profile owner's User doc.
 * Fire-and-forget — errors are swallowed via `.catch(() => {})` so
 * the navigation is NEVER blocked (analytics is best-effort).
 *
 * Why a client component?
 *   The public profile page (`/p/[username]/page.tsx`) is a Server
 *   Component (ISR + `generateMetadata`). The button needs to:
 *     1. Set a browser cookie (client-only API — `document.cookie`).
 *     2. Navigate to a new URL (client-side router for SPA-like UX).
 *     3. SPEC-119 Task E: fire the click tracking `fetch` (browser-only).
 *   Both require `'use client'`. The Server Component renders the
 *   button as a static anchor (no JS) so it works without JS (progressive
 *   enhancement) — when JS loads, the click handler takes over to set the
 *   cookie + navigate via the router.
 *
 * §6 Anti-Invención:
 *   - Cookie name + attributes are owned by `@/lib/referral-cookie`
 *     (`setReferralCookie`) — single source of truth, mirrors the
 *     existing `/r/[code]` impl.
 *   - The Market URL `/market?ref={username}` is built from the public
 *     profile's `username` param (the canonical Profileia Hub URL
 *     pattern, SPEC-NEGOCIO §8.1).
 *   - NO `as any` (P-041), NO `console.*` (P-042), NO refs to docs in
 *     UI strings (P-083).
 *
 * Spec ref: SPEC-094 §1.4, SPEC-119 Task E, SPEC-NEGOCIO §8.1 + §8.3.
 */

export interface VisitMarketButtonProps {
  /** The profile owner's username (sets the cookie + builds the URL). */
  username: string;
}

export function VisitMarketButton({ username }: VisitMarketButtonProps) {
  const router = useRouter();

  const handleClick = useCallback(() => {
    // SPEC-094 §1.4: set the `profileia_ref` cookie BEFORE navigating
    // so the visitor's eventual registration is attributed to this
    // profile owner (SPEC-NEGOCIO §8.3 — 30-day attribution window).
    // The cookie is also set by `<ReferralRefCapture explicitCode=... />`
    // on mount, but we set it again here in case the visitor cleared
    // cookies mid-session + to be defensive (the click is the strongest
    // signal of intent to engage with this profile owner's Market).
    setReferralCookie(username);
    // PR3 §3.2 (V107-H PR3): fire-and-forget HubVisit beacon with
    // ctaClicked='myMarket' so the click is recorded in the HubVisit
    // collection (consumed by getTierBenchmark + getHubToMarketConversion
    // on the backend, Sub-PR 2 commit 9b72914e). The 'myMarket' enum value
    // was added to HubVisitCta by Sub-PR 1 commit ff76b9f5.
    // sendHubVisitBeacon is sync (void) — the internal fetch uses
    // keepalive:true + .catch(() => {}) so it NEVER blocks the click
    // handler, NEVER throws, and survives the navigation below.
    sendHubVisitBeacon({
      hubOwnerUsername: username,
      ctaClicked: 'myMarket',
    });
    // SPEC-119 Task E: fire-and-forget POST to record the click for
    // analytics. The fetch is NOT awaited — the navigation below proceeds
    // immediately. Errors are swallowed silently so the navigation is
    // NEVER blocked (analytics is best-effort + must NEVER break the UX).
    fetch(`/api/public/${encodeURIComponent(username)}/click`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ target: 'market' }),
    }).catch(() => {
      // Intentionally swallow — analytics must NEVER break the UX.
    });
    // Navigate to /market?ref={username} — the `?ref=` param is also
    // captured by the /market page's own `<ReferralRefCapture />`
    // effect (redundant with the cookie set above, but defense-in-depth:
    // if cookies are blocked, the `?ref=` param is still a fallback
    // attribution signal via the register form's URL param reader).
    // PR #710: open in new tab so visitor doesn't lose the digital card.
    window.open(`/market?ref=${encodeURIComponent(username)}`, '_blank', 'noopener,noreferrer');
  }, [username]);

  return (
    <button
      type="button"
      onClick={handleClick}
      className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-[#10B981] hover:bg-[#10B981]/90 text-white px-4 py-2.5 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      aria-label={`Visitar el Market de @${username} (abre el catálogo público con tu cookie de referido)`}
    >
      <ShoppingBag className="size-4" aria-hidden="true" />
      Visita mi Market
    </button>
  );
}
